/**
 * PhaserVexFlowBackend
 *
 * A VexFlow backend implementation that renders music notation using Phaser's
 * Graphics API instead of Canvas or SVG. This allows OSMD to render directly
 * into a Phaser scene for interactive music applications.
 */

import Phaser from 'phaser';
// VexFlow import - resolved by Vite alias to OSMD's bundled VexFlow 1.2.93
import Vex from 'vexflow';
import VF = Vex.Flow;
import {
  VexFlowBackend,
  FontStyles,
  Fonts,
  RectangleF2D,
  PointF2D,
  BackendType,
  EngravingRules,
  VexFlowGraphicalNote,
} from '@museflow-ai/opensheetmusicdisplay';
import { PhaserVexFlowContext } from './PhaserVexFlowContext';

/**
 * VexFlow backend that renders to a Phaser scene.
 *
 * Unlike SVG and Canvas backends which create their own DOM elements,
 * the Phaser backend renders into an existing Phaser scene using
 * Graphics and Text game objects.
 */
export class PhaserVexFlowBackend extends VexFlowBackend {
  private scene: Phaser.Scene;
  private phaserContainer: Phaser.GameObjects.Container;
  private graphics: Phaser.GameObjects.Graphics;
  private context: PhaserVexFlowContext;
  private zoom: number = 1;

  // Store text objects for cleanup
  private textObjects: Phaser.GameObjects.Text[] = [];

  // Store rendered elements for interaction
  private renderedLines: Array<{
    start: PointF2D;
    end: PointF2D;
    graphics: Phaser.GameObjects.Graphics;
  }> = [];

  constructor(rules: EngravingRules, scene: Phaser.Scene) {
    super();
    this.rules = rules;
    this.scene = scene;

    // Validate scene is properly initialized
    if (!scene) {
      throw new Error('PhaserVexFlowBackend: scene is undefined');
    }
    if (!scene.add) {
      throw new Error(
        `PhaserVexFlowBackend: scene.add is undefined. Scene key: ${scene.scene?.key}`,
      );
    }

    // Initialize graphics and container
    this.graphics = scene.add.graphics();
    this.phaserContainer = scene.add.container(0, 0);
    this.phaserContainer.add(this.graphics);

    // Create the context wrapper
    this.context = new PhaserVexFlowContext(scene, this.graphics, this.phaserContainer);
    this.context.setDefaultMusicColor(this.rules?.DefaultColorMusic || '#000000');

    // Note: graphicalMusicPage will be set by the caller (phaserBackendFactory)
    // Don't create a default one here as it causes issues
  }

  /**
   * Initialize the backend with a container and zoom level.
   * For Phaser backend, the container parameter is ignored as we use
   * the Phaser scene passed to the constructor.
   *
   * Note: OSMD's drawer will call backend.scale(zoom) separately after initialize().
   * The zoom is used by OSMD to calculate coordinates before passing them to us.
   * We store zoom for reference but don't apply it to container scale since
   * coordinates are already scaled.
   */
  initialize(container: HTMLElement, zoom: number): void {
    this.zoom = zoom;

    // Create mock HTML elements for compatibility with VexFlowBackend base class
    // These are required by OSMD's VexFlowMusicSheetDrawer for certain operations
    this.canvas = document.createElement('div');
    this.canvas.id = 'osmdPhaserPage' + (this.graphicalMusicPage?.PageNumber ?? 1);
    this.inner = this.canvas;

    // Set a mock style object to prevent errors when OSMD accesses style properties
    this.inner.style.position = 'relative';
    this.inner.style.display = 'none'; // Hidden since we're not using it for rendering

    // Add to container to satisfy any parent/child checks
    if (container) {
      container.appendChild(this.inner);
    }

    // Store dimensions
    this.width = this.scene?.scale?.width ?? 1920;
    this.height = this.scene?.scale?.height ?? 1080;

    // Clear any existing content
    this.clear();

    // Note: We do NOT apply scale to the Phaser container here.
    // OSMD/VexFlow already passes coordinates in the zoomed coordinate space.
  }

  /**
   * Get the VexFlow rendering context.
   * Returns our custom PhaserVexFlowContext that implements Vex.IRenderContext.
   */
  getContext(): Vex.IRenderContext {
    return this.context as unknown as Vex.IRenderContext;
  }

  /**
   * Get the Phaser-specific context for accessing custom methods.
   * Use this when you need to access element overrides or other Phaser-specific features.
   */
  getPhaserContext(): PhaserVexFlowContext {
    return this.context;
  }

  /**
   * Returns the VexFlow backend type.
   * We use CANVAS as there's no PHASER type in VexFlow.
   */
  getVexflowBackendType(): VF.Renderer.Backends {
    // Use CANVAS since VexFlow doesn't have a PHASER type
    // The actual rendering is handled by our context
    return VF.Renderer.Backends.CANVAS;
  }

  /**
   * Returns the OSMD backend type.
   */
  getOSMDBackendType(): BackendType {
    return BackendType.Phaser;
  }

  /**
   * Get the canvas size (height).
   * For Phaser, returns the container bounds height.
   */
  getCanvasSize(): number {
    return this.height || this.scene.scale.height;
  }

  /**
   * Apply uniform scale transformation.
   * Note: OSMD's drawer calls this, but we do NOT apply it to the Phaser container
   * because OSMD/VexFlow already passes coordinates in the zoomed coordinate space.
   * We just store the zoom for reference.
   */
  scale(scaleFactor: number): void {
    this.zoom = scaleFactor;
    // Note: We intentionally do NOT scale the phaserContainer here.
    // VexFlow drawing methods receive pre-scaled coordinates from OSMD.
  }

  /**
   * Translate the rendering origin.
   */
  translate(x: number, y: number): void {
    this.phaserContainer.setPosition(this.phaserContainer.x + x, this.phaserContainer.y + y);
  }

  /**
   * Clear all rendered content.
   */
  clear(): void {
    // Clear the graphics
    this.graphics.clear();

    // Destroy text objects
    this.textObjects.forEach((textObject) => {
      textObject.destroy();
    });
    this.textObjects = [];

    // Clear rendered elements tracking
    this.renderedLines = [];

    // Clear the context
    this.context.clear();

    // Set background color if defined
    if (this.rules?.PageBackgroundColor) {
      const color = this.parseColorHex(this.rules.PageBackgroundColor);
      this.graphics.fillStyle(color, 1);
      this.graphics.fillRect(0, 0, this.width || 1920, this.height || 1080);
    }
  }

  /**
   * Free memory and clean up resources.
   */
  free(): void {
    this.clear();

    if (this.graphics) {
      this.graphics.destroy();
    }

    if (this.phaserContainer) {
      this.phaserContainer.destroy();
    }
  }

  /**
   * Resize the backend canvas.
   * Override base class to avoid calling this.renderer.resize() since we don't use VexFlow's renderer.
   */
  resize(width: number, height: number): void {
    this.width = width;
    this.height = height;
    // Phaser scene handles resizing, we just track dimensions
    // Don't call super.resize() as it expects this.renderer to exist
  }

  /**
   * Render text at the specified position.
   * Note: OSMD passes fontHeight already in pixels (not OSMD units).
   * The screenPosition is already in the zoomed coordinate space.
   */
  renderText(
    fontHeight: number,
    fontStyle: FontStyles,
    font: Fonts,
    text: string,
    heightInPixel: number,
    screenPosition: PointF2D,
    color?: string,
    fontFamily?: string,
  ): Node {
    // fontHeight is already in pixels (OSMD converts before calling)
    const fontSizePixels = fontHeight;
    const fontFamilyResolved = fontFamily || this.getFontFamily(font);
    const fontStyleStr = this.getFontStyleString(fontStyle);

    // screenPosition is already in pixels (OSMD calculates this)
    const textObject = this.scene.add.text(
      screenPosition.x,
      screenPosition.y + heightInPixel,
      text,
      {
        fontFamily: fontFamilyResolved,
        fontSize: `${fontSizePixels}px`,
        fontStyle: fontStyleStr,
        color: color || this.rules?.DefaultColorMusic || '#000000',
      },
    );

    textObject.setOrigin(0, 1); // Baseline alignment
    this.phaserContainer.add(textObject);
    this.textObjects.push(textObject);

    return null as unknown as Node; // Phaser doesn't return DOM nodes
  }

  /**
   * Render a filled rectangle.
   * Note: rectangle coordinates are already in pixels from OSMD.
   */
  renderRectangle(
    rectangle: RectangleF2D,
    _styleId: number,
    colorHex: string,
    alpha: number = 1,
  ): Node {
    const color = this.parseColorHex(colorHex || '#000000');

    this.graphics.fillStyle(color, alpha);
    this.graphics.fillRect(rectangle.x, rectangle.y, rectangle.width, rectangle.height);

    return null as unknown as Node;
  }

  /**
   * Render a line between two points.
   * Note: coordinates are already in pixels from OSMD.
   */
  renderLine(
    start: PointF2D,
    stop: PointF2D,
    color: string = '#000000',
    lineWidth: number = 1,
    _id?: string,
  ): Node {
    const colorNum = this.parseColorHex(color);

    this.graphics.lineStyle(lineWidth, colorNum, 1);
    this.graphics.beginPath();
    this.graphics.moveTo(start.x, start.y);
    this.graphics.lineTo(stop.x, stop.y);
    this.graphics.strokePath();

    // Track for potential interaction
    this.renderedLines.push({ start, end: stop, graphics: this.graphics });

    return null as unknown as Node;
  }

  /**
   * Render a Bezier curve (used for slurs and ties).
   * VexFlow passes 8 points defining the outer and inner edges of the curve.
   * Note: coordinates are already in pixels from OSMD.
   */
  renderCurve(points: PointF2D[], _isSlur?: boolean, _startNote?: VexFlowGraphicalNote): Node {
    if (points.length < 8) {
      return null as unknown as Node;
    }

    const color = this.parseColorHex(this.rules?.DefaultColorMusic || '#000000');

    this.graphics.fillStyle(color, 1);
    this.graphics.beginPath();

    // Outer curve: points[0] -> points[1], points[2] -> points[3]
    this.graphics.moveTo(points[0].x, points[0].y);
    this.drawBezierCurve(
      points[0].x,
      points[0].y,
      points[1].x,
      points[1].y,
      points[2].x,
      points[2].y,
      points[3].x,
      points[3].y,
    );

    // Line to inner curve start
    this.graphics.lineTo(points[7].x, points[7].y);

    // Inner curve (reverse): points[7] -> points[6], points[5] -> points[4]
    this.drawBezierCurve(
      points[7].x,
      points[7].y,
      points[6].x,
      points[6].y,
      points[5].x,
      points[5].y,
      points[4].x,
      points[4].y,
    );

    // Close path back to start
    this.graphics.lineTo(points[0].x, points[0].y);
    this.graphics.closePath();
    this.graphics.fillPath();

    return null as unknown as Node;
  }

  /**
   * Render a path of connected points.
   * Note: coordinates are already in pixels from OSMD.
   */
  renderPath(points: PointF2D[], fill: boolean = true, _id?: string): Node {
    if (points.length === 0) {
      return null as unknown as Node;
    }

    const color = this.parseColorHex(this.rules?.DefaultColorMusic || '#000000');

    if (fill) {
      this.graphics.fillStyle(color, 1);
    } else {
      this.graphics.lineStyle(1, color, 1);
    }

    this.graphics.beginPath();
    this.graphics.moveTo(points[0].x, points[0].y);

    for (let i = 1; i < points.length; i++) {
      this.graphics.lineTo(points[i].x, points[i].y);
    }

    this.graphics.closePath();

    if (fill) {
      this.graphics.fillPath();
    } else {
      this.graphics.strokePath();
    }

    return null as unknown as Node;
  }

  /**
   * Helper to draw a bezier curve using line segment approximation.
   */
  private drawBezierCurve(
    startX: number,
    startY: number,
    cp1x: number,
    cp1y: number,
    cp2x: number,
    cp2y: number,
    endX: number,
    endY: number,
    segments: number = 20,
  ): void {
    for (let i = 1; i <= segments; i++) {
      const t = i / segments;
      const t2 = t * t;
      const t3 = t2 * t;
      const mt = 1 - t;
      const mt2 = mt * mt;
      const mt3 = mt2 * mt;

      const x = mt3 * startX + 3 * mt2 * t * cp1x + 3 * mt * t2 * cp2x + t3 * endX;
      const y = mt3 * startY + 3 * mt2 * t * cp1y + 3 * mt * t2 * cp2y + t3 * endY;

      this.graphics.lineTo(x, y);
    }
  }

  /**
   * Parse a hex color string to a number.
   */
  private parseColorHex(colorHex: string): number {
    if (!colorHex && !this.rules?.DefaultColorMusic) {
      return 0x000000;
    }

    let hex = this.shouldUseDefaultMusicColor(colorHex)
      ? this.rules?.DefaultColorMusic || '#000000'
      : colorHex;

    hex = hex.trim().toLowerCase();
    if (hex === 'black') {
      return 0x000000;
    }
    if (hex === 'white') {
      return 0xffffff;
    }
    if (hex === 'red') {
      return 0xff0000;
    }
    if (hex === 'green') {
      return 0x00ff00;
    }
    if (hex === 'blue') {
      return 0x0000ff;
    }

    if (hex.startsWith('#')) {
      hex = hex.slice(1);
    }

    // Handle 3-character hex
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((c) => c + c)
        .join('');
    }

    // Take only RGB portion (ignore alpha)
    if (hex.length >= 6) {
      hex = hex.slice(0, 6);
    }

    const parsed = parseInt(hex, 16);
    return Number.isNaN(parsed) ? 0x000000 : parsed;
  }

  private shouldUseDefaultMusicColor(colorHex: string): boolean {
    if (!colorHex) {
      return true;
    }
    const normalized = colorHex.trim().replace(/\s/g, '').toLowerCase();
    if (normalized === 'black') return true;
    if (/^rgba?\(0,0,0(?:,1(?:\.0)?)?\)$/.test(normalized)) return true;

    const hex = normalized.startsWith('#') ? normalized.slice(1) : normalized;
    if (hex.length === 3) {
      return hex === '000';
    }

    return hex.length >= 6 && hex.slice(0, 6) === '000000';
  }

  /**
   * Get font family string from Fonts enum.
   * Note: OSMD Fonts enum only has TimesNewRoman and Kokila (text fonts).
   * Music fonts (Bravura, Gonville, Petaluma) are handled by VexFlow directly.
   */
  private getFontFamily(font: Fonts): string {
    switch (font) {
      case Fonts.TimesNewRoman:
        return 'Times New Roman';
      case Fonts.Kokila:
        return 'Kokila';
      default:
        return 'Times New Roman';
    }
  }

  /**
   * Get font style string from FontStyles enum.
   */
  private getFontStyleString(fontStyle: FontStyles): string {
    switch (fontStyle) {
      case FontStyles.Bold:
        return 'bold';
      case FontStyles.Italic:
        return 'italic';
      case FontStyles.BoldItalic:
        return 'bold italic';
      default:
        return 'normal';
    }
  }

  /**
   * Get the Phaser container for external access.
   */
  getContainer(): Phaser.GameObjects.Container {
    return this.phaserContainer;
  }

  /**
   * Get the Phaser graphics object for external access.
   */
  getGraphics(): Phaser.GameObjects.Graphics {
    return this.graphics;
  }

  /**
   * Get the Phaser scene.
   */
  getScene(): Phaser.Scene {
    return this.scene;
  }
}
